import React, { useState, useEffect } from 'react';
import { MessageSquare, ChevronRight, X, Paperclip, Link2, User, Car, Store, ExternalLink } from 'lucide-react';
import apiService from '../../apiService';
import { useLiveRefresh } from '../../liveRefresh';

const ACTIVITY_LABELS = { ride: 'Course', delivery: 'Commande', parcel: 'Colis', rental: 'Location', beauty: 'Rendez-vous beauté', lodging: 'Hébergement' };
const STATUS_LABELS = { open: 'Ouvert', in_progress: 'En cours', closed: 'Fermé' };
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '');

// Le client a-t-il écrit depuis la dernière ouverture par l'admin ? (pastille « Nouveau »)
const needsAttention = (t) => {
  const lastUser = [...(t.messages || [])].reverse().find(m => m.sender_type === 'user');
  const lastAt = lastUser ? lastUser.created_at : (t.createdAt || t.created_at);
  return !t.admin_seen_at || new Date(lastAt) > new Date(t.admin_seen_at);
};

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);      // détail de l'activité jointe (modale)
  const [activityLoading, setActivityLoading] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/support?status=${filter}`, { userType: 'admin' });
      if (response.success) setTickets(response.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, [filter]);
  useLiveRefresh(() => fetchTickets(), ['support_ticket', 'support_reply']);

  // Ouverture d'un ticket : recharge le détail (URL signées fraîches des images, marque « vu » côté admin)
  const openTicket = async (ticket) => {
    setSelected(ticket);
    try {
      const response = await apiService.get(`/support/${ticket._id}`, { userType: 'admin' });
      if (response.success) {
        setSelected(response.data);
        setTickets(prev => prev.map(t => (t._id === ticket._id ? { ...t, admin_seen_at: new Date().toISOString() } : t)));
      }
    } catch {}
  };

  const handleReply = async () => {
    if (!reply.trim() || !selected || sending) return;
    setSending(true);
    try {
      const r = await apiService.post(`/support/${selected._id}/messages`, { message: reply.trim() }, { userType: 'admin' });
      if (r.success) { setReply(''); setSelected(r.data); fetchTickets(); }
    } catch {} finally { setSending(false); }
  };

  const handleClose = async (ticketId) => {
    if (!window.confirm('Clôturer ce ticket ? Le client en sera informé.')) return;
    try {
      await apiService.patch(`/support/${ticketId}`, { status: 'closed' }, { userType: 'admin' });
      fetchTickets();
      setSelected(null);
    } catch {}
  };

  // Activité jointe → modale de détail complet (client, chauffeur, fournisseur, montants, dates…)
  const openActivity = async (act) => {
    if (!act?.type || !act?.id) return;
    setActivity({ loading: true, type: act.type });
    setActivityLoading(true);
    try {
      const r = await apiService.get(`/support/activity/${act.type}/${encodeURIComponent(act.id)}`, { userType: 'admin' });
      setActivity(r.success ? r.data : { error: r.error || 'Détail indisponible' });
    } catch (e) {
      setActivity({ error: e?.message || 'Détail indisponible' });
    } finally { setActivityLoading(false); }
  };

  const chip = (text, bg, fg) => (
    <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '999px', background: bg, color: fg, fontSize: '0.7rem', fontWeight: 700 }}>{text}</span>
  );

  return (
    <div>
      <div style={{marginBottom:'1.5rem'}}>
        <h2 style={{margin:0,fontSize:'1.5rem',fontWeight:'700',color:'#1E2229'}}>🎧 Support</h2>
        <p style={{margin:'0.25rem 0 0',fontSize:'0.875rem',color:'#9AA0A8'}}>Signalements et tickets des utilisateurs — vos réponses arrivent dans l'app (page Aide & Support + notification).</p>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:'0.5rem',marginBottom:'1.25rem'}}>
        {[{key:'open',label:'Ouverts'},{key:'in_progress',label:'En cours'},{key:'closed',label:'Fermés'}].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding:'0.5rem 1rem', borderRadius:'12px', border: filter===f.key ? 'none' : '1px solid #E9EBEF',
            background: filter===f.key ? '#FFB900' : 'white', color: filter===f.key ? 'white' : '#5C636E',
            fontWeight:'600', fontSize:'0.875rem', cursor:'pointer'
          }}>{f.label}</button>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns: selected ? '1fr 1fr' : '1fr',gap:'1.5rem'}}>
        {/* Tickets list */}
        <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
          {loading && tickets.length === 0 ? (
            <p style={{color:'#9AA0A8',textAlign:'center',padding:'2rem'}}>Chargement…</p>
          ) : tickets.length === 0 ? (
            <div style={{background:'white',borderRadius:'16px',padding:'3rem',textAlign:'center',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
              <MessageSquare size={36} color="#DCE0E6" style={{marginBottom:'0.5rem'}}/>
              <p style={{color:'#9AA0A8',margin:0}}>Aucun ticket</p>
            </div>
          ) : tickets.map(ticket => (
            <button key={ticket._id} onClick={() => openTicket(ticket)} style={{
              width:'100%',background:'white',borderRadius:'16px',padding:'1rem',textAlign:'left',
              boxShadow:'0 1px 3px rgba(0,0,0,0.08)',border: selected?._id === ticket._id ? '2px solid #FFB900' : '1px solid transparent',
              cursor:'pointer',transition:'box-shadow 0.2s'
            }}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.35rem',gap:'0.5rem'}}>
                <span style={{fontWeight:'600',fontSize:'0.95rem',color:'#1E2229',display:'flex',alignItems:'center',gap:'0.4rem'}}>
                  {ticket.status !== 'closed' && needsAttention(ticket) && <span title="Nouveau message du client" style={{width:8,height:8,borderRadius:4,background:'#F54119',display:'inline-block'}}/>}
                  {ticket.subject}
                </span>
                <span style={{display:'flex',alignItems:'center',gap:'0.4rem'}}>
                  {ticket.public_id && <span style={{fontFamily:'monospace',fontSize:'0.7rem',color:'#9AA0A8'}}>{ticket.public_id}</span>}
                  <ChevronRight size={16} color="#DCE0E6"/>
                </span>
              </div>
              <p style={{margin:'0 0 0.25rem',fontSize:'0.75rem',color:'#9AA0A8'}}>
                {ticket.actor_role === 'supplier' ? `🏪 ${ticket.supplier_name || ticket.user_name}` : ticket.actor_role === 'driver' ? `🚖 ${ticket.user_name || 'Chauffeur'}` : (ticket.user_name || 'Utilisateur')} · {new Date(ticket.createdAt || ticket.created_at).toLocaleDateString('fr-FR')}{ticket.app_type ? ` · ${ticket.app_type}` : ''}
              </p>
              <p style={{margin:0,fontSize:'0.875rem',color:'#5C636E',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ticket.message}</p>
              {(ticket.attachments?.length > 0 || ticket.activity?.type || ticket.messages?.length > 0) && (
                <div style={{display:'flex',gap:'0.6rem',marginTop:'0.4rem',fontSize:'0.72rem',color:'#9AA0A8',alignItems:'center'}}>
                  {ticket.attachments?.length > 0 && <span style={{display:'inline-flex',alignItems:'center',gap:'0.2rem'}}><Paperclip size={12}/>{ticket.attachments.length}</span>}
                  {ticket.activity?.type && <span style={{display:'inline-flex',alignItems:'center',gap:'0.2rem'}}><Link2 size={12}/>{ACTIVITY_LABELS[ticket.activity.type] || ticket.activity.type}</span>}
                  {ticket.messages?.length > 0 && <span style={{display:'inline-flex',alignItems:'center',gap:'0.2rem'}}><MessageSquare size={12}/>{ticket.messages.length}</span>}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)',position:'sticky',top:'1.5rem',alignSelf:'start'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'0.75rem',gap:'0.5rem'}}>
              <div>
                <h3 style={{margin:0,fontSize:'1.1rem',fontWeight:'700',color:'#1E2229'}}>{selected.subject}</h3>
                <div style={{display:'flex',gap:'0.4rem',alignItems:'center',marginTop:'0.3rem',flexWrap:'wrap'}}>
                  {chip(STATUS_LABELS[selected.status] || selected.status, selected.status === 'closed' ? '#EEF0F3' : '#FFF4D6', selected.status === 'closed' ? '#5C636E' : '#8A5A00')}
                  {selected.public_id && <span style={{fontFamily:'monospace',fontSize:'0.72rem',color:'#9AA0A8'}}>{selected.public_id}</span>}
                  <span style={{fontSize:'0.72rem',color:'#9AA0A8'}}>{selected.actor_role === 'supplier' ? `🏪 Boutique ${selected.supplier_name || ''}` : selected.actor_role === 'driver' ? `🚖 Chauffeur ${selected.user_name || ''}` : (selected.user_name || 'Utilisateur')}{selected.user_phone ? ` · ${selected.user_phone}` : ''}{selected.user_id ? ` · ${selected.user_id}` : ''}</span>
                </div>
              </div>
              <div style={{display:'flex',gap:'0.5rem',flex:'none'}}>
                {selected.status !== 'closed' && (
                  <button onClick={() => handleClose(selected._id)} style={{
                    padding:'0.35rem 0.75rem',borderRadius:'8px',background:'#fef2f2',color:'#DC2626',
                    border:'none',fontSize:'0.75rem',fontWeight:'600',cursor:'pointer'
                  }}>Clôturer</button>
                )}
                <button onClick={() => setSelected(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0A8'}}><X size={18}/></button>
              </div>
            </div>

            {/* Activité désignée par le client → cliquable : détail complet */}
            {selected.activity?.type && (
              <button onClick={() => openActivity(selected.activity)} title="Voir le détail de cette activité" style={{
                width:'100%',display:'flex',alignItems:'center',gap:'0.5rem',background:'#FFF8E6',border:'1px solid #FFE7A3',borderRadius:'12px',
                padding:'0.6rem 0.75rem',marginBottom:'0.75rem',fontSize:'0.8rem',color:'#1E2229',cursor:'pointer',textAlign:'left'
              }}>
                <Link2 size={14} color="#B98400"/>
                <span style={{flex:1}}><strong>{ACTIVITY_LABELS[selected.activity.type] || selected.activity.type}</strong>
                  {selected.activity.public_id ? ` ${selected.activity.public_id}` : ''}{selected.activity.label ? ` — ${selected.activity.label}` : ''}
                  {selected.activity.date ? ` · ${fmt(selected.activity.date)}` : ''}</span>
                <span style={{display:'inline-flex',alignItems:'center',gap:'0.25rem',color:'#B98400',fontWeight:700,fontSize:'0.75rem'}}>Détails <ExternalLink size={12}/></span>
              </button>
            )}
            {/* Pièces jointes (captures / photos) */}
            {selected.attachments?.length > 0 && (
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap',marginBottom:'0.75rem'}}>
                {selected.attachments.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer" title={`Pièce jointe ${i + 1}`}>
                    <img src={a.url} alt={`Pièce jointe ${i + 1}`} style={{width:'96px',height:'96px',objectFit:'cover',borderRadius:'10px',border:'1px solid #E9EBEF'}}/>
                  </a>
                ))}
              </div>
            )}

            {/* Fil de discussion */}
            <div style={{maxHeight:'400px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'0.75rem',marginBottom:'1rem'}}>
              <div style={{background:'#F7F8FA',borderRadius:'12px',padding:'0.75rem'}}>
                <p style={{margin:'0 0 0.25rem',fontSize:'0.75rem',color:'#9AA0A8'}}>
                  {selected.user_name || 'Utilisateur'} · {fmt(selected.createdAt || selected.created_at)}
                </p>
                <p style={{margin:0,fontSize:'0.875rem',color:'#1E2229',whiteSpace:'pre-wrap'}}>{selected.message}</p>
              </div>
              {(selected.messages || []).map((msg, i) => (
                <div key={i} style={{
                  background: msg.sender_type === 'admin' ? '#fff7ed' : '#F7F8FA',
                  borderRadius:'12px',padding:'0.75rem',
                  marginLeft: msg.sender_type === 'admin' ? '1rem' : 0,
                  marginRight: msg.sender_type !== 'admin' ? '1rem' : 0,
                }}>
                  <p style={{margin:'0 0 0.25rem',fontSize:'0.75rem',color:'#9AA0A8'}}>
                    {msg.sender_type === 'admin' ? 'Équipe Mbiyo' : (selected.user_name || 'Utilisateur')} · {fmt(msg.created_at)}
                  </p>
                  <p style={{margin:0,fontSize:'0.875rem',color:'#1E2229',whiteSpace:'pre-wrap'}}>{msg.message}</p>
                </div>
              ))}
            </div>

            {/* Réponse — le client la reçoit dans l'app (Aide & Support) + notification push */}
            {selected.status !== 'closed' ? (
              <div style={{display:'flex',gap:'0.5rem'}}>
                <input value={reply} onChange={e => setReply(e.target.value)}
                  placeholder="Votre réponse au client…" onKeyDown={e => e.key === 'Enter' && handleReply()}
                  style={{flex:1,border:'1px solid #E9EBEF',borderRadius:'12px',padding:'0.6rem 0.75rem',fontSize:'0.875rem',outline:'none'}}/>
                <button onClick={handleReply} disabled={sending || !reply.trim()} style={{
                  padding:'0.6rem 1rem',borderRadius:'12px',border:'none',background:'#FFB900',color:'white',
                  fontWeight:'600',fontSize:'0.875rem',cursor:'pointer',opacity: sending || !reply.trim() ? 0.6 : 1
                }}>{sending ? 'Envoi…' : 'Envoyer'}</button>
              </div>
            ) : (
              <p style={{margin:0,fontSize:'0.8rem',color:'#9AA0A8',textAlign:'center'}}>Ticket clôturé{selected.closed_at ? ` le ${fmt(selected.closed_at)}` : ''}.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Modale : détail complet de l'activité jointe ── */}
      {activity && (
        <div onClick={() => setActivity(null)} style={{position:'fixed',inset:0,background:'rgba(17,20,24,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <div onClick={e => e.stopPropagation()} style={{background:'white',borderRadius:'18px',width:'min(760px, 100%)',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'1rem 1.25rem',borderBottom:'1px solid #E9EBEF',position:'sticky',top:0,background:'white'}}>
              <div>
                <h3 style={{margin:0,fontSize:'1.05rem',fontWeight:700,color:'#1E2229'}}>
                  {activity.label || ACTIVITY_LABELS[activity.type] || 'Activité'}{activity.public_id ? ` ${activity.public_id}` : ''}
                </h3>
                {activity.status && <div style={{marginTop:'0.3rem',display:'flex',gap:'0.4rem',alignItems:'center'}}>
                  {chip(activity.status, '#EEF0F3', '#414852')}
                  <span style={{fontSize:'0.72rem',color:'#9AA0A8'}}>{fmt(activity.created_at)}</span>
                  <span style={{fontFamily:'monospace',fontSize:'0.7rem',color:'#9AA0A8'}}>{activity.id}</span>
                </div>}
              </div>
              <button onClick={() => setActivity(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0A8'}}><X size={20}/></button>
            </div>

            <div style={{padding:'1rem 1.25rem 1.25rem'}}>
              {activityLoading || activity.loading ? (
                <p style={{color:'#9AA0A8',textAlign:'center',padding:'2rem'}}>Chargement du détail…</p>
              ) : activity.error ? (
                <p style={{color:'#DC2626',textAlign:'center',padding:'2rem'}}>{activity.error}</p>
              ) : (
                <>
                  {/* Personnes impliquées */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:'0.75rem',marginBottom:'1rem'}}>
                    {[
                      { key: 'client', title: 'Client', Icon: User, p: activity.people?.client, lines: p => [p.name, p.phone, p.email, p.public_id || p.user_id] },
                      { key: 'driver', title: activity.type === 'ride' ? 'Chauffeur' : 'Livreur / Chauffeur', Icon: Car, p: activity.people?.driver,
                        lines: p => [p.name, p.phone, [p.vehicle_brand, p.vehicle_model, p.vehicle_plate].filter(Boolean).join(' '), p.public_id || p.driver_id] },
                      { key: 'supplier', title: activity.type === 'lodging' ? 'Hôte' : 'Fournisseur', Icon: Store, p: activity.people?.supplier,
                        lines: p => [p.business_name, p.business_type, p.phone, p.email, p.address, p.public_id || p.supplier_id] },
                    ].filter(c => c.p || (c.key === 'driver' && ['ride','delivery','parcel','rental'].includes(activity.type)) || (c.key === 'supplier' && ['delivery','rental','beauty','lodging'].includes(activity.type)))
                     .map(({ key, title, Icon, p, lines }) => (
                      <div key={key} style={{border:'1px solid #E9EBEF',borderRadius:'12px',padding:'0.75rem'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'0.4rem',fontSize:'0.72rem',fontWeight:700,color:'#9AA0A8',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'0.4rem'}}><Icon size={13}/>{title}</div>
                        {p ? lines(p).filter(Boolean).map((l, i) => (
                          <p key={i} style={{margin:0,fontSize: i === 0 ? '0.9rem' : '0.8rem',fontWeight: i === 0 ? 700 : 400,color: i === 0 ? '#1E2229' : '#5C636E'}}>{l}</p>
                        )) : <p style={{margin:0,fontSize:'0.8rem',color:'#9AA0A8'}}>— non attribué —</p>}
                      </div>
                    ))}
                  </div>

                  {/* Faits : trajet, montants, dates, statuts… */}
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
                    <tbody>
                      {(activity.facts || []).map((f, i) => (
                        <tr key={i} style={{borderTop:'1px solid #F0F2F5'}}>
                          <td style={{padding:'0.45rem 0.5rem 0.45rem 0',color:'#9AA0A8',whiteSpace:'nowrap',verticalAlign:'top',width:'38%'}}>{f.label}</td>
                          <td style={{padding:'0.45rem 0',color:'#1E2229',wordBreak:'break-word'}}>{String(f.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
